import React from "react";
import { Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export function PricingSection() {
  const { t } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );

  const plans = [
    {
      name: t("pricingSection.plans.free.name"),
      price: t("pricingSection.plans.free.price"),
      yearlyPrice: t("pricingSection.plans.free.price"),
      description: t("pricingSection.plans.free.description"),
      features: t("pricingSection.plans.free.features", {
        returnObjects: true,
      }) as string[],
      highlighted: false,
      isLifetime: false,
      billingPeriod: "monthly",
    },
    {
      name: t("pricingSection.plans.professional.name"),
      price: t("pricingSection.plans.professional.price"),
      yearlyPrice: t("pricingSection.plans.professional.yearlyPrice"),
      description: t("pricingSection.plans.professional.description"),
      features: t("pricingSection.plans.professional.features", {
        returnObjects: true,
      }) as string[],
      highlighted: true,
      isLifetime: false,
      billingPeriod: billingPeriod,
    },
    {
      name: t("pricingSection.plans.enterprise.name"),
      price: t("pricingSection.plans.enterprise.price"),
      yearlyPrice: t("pricingSection.plans.enterprise.yearlyPrice"),
      description: t("pricingSection.plans.enterprise.description"),
      features: t("pricingSection.plans.enterprise.features", {
        returnObjects: true,
      }) as string[],
      highlighted: false,
      isLifetime: false,
      billingPeriod: billingPeriod,
    },
    {
      name: t("pricingSection.plans.lifetime.name"),
      price: t("pricingSection.plans.lifetime.price"),
      yearlyPrice: t("pricingSection.plans.lifetime.price"),
      description: t("pricingSection.plans.lifetime.description"),
      features: t("pricingSection.plans.lifetime.features", {
        returnObjects: true,
      }) as string[],
      highlighted: false,
      isLifetime: true,
      billingPeriod: "lifetime",
      badge: t("pricingSection.plans.lifetime.badge"),
    },
  ];

  const handleSubscribe = async (planName: string) => {
    // TODO: Implement Stripe checkout integration
    console.log(`Subscribe to ${planName} plan`);
  };

  return (
    <section id="pricing" className="py-16 md:py-24 bg-muted/50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight animate-fade-in">
            {t("pricingSection.title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground animate-fade-in animate-delay-300">
            {t("pricingSection.subtitle")}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-muted p-1 rounded-lg">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("pricingSection.billingToggle.monthly")}
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === "yearly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("pricingSection.billingToggle.yearly")}
              <span className="ml-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                {t("pricingSection.billingToggle.discount")}
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`animate-zoom-in ${
                plan.highlighted
                  ? "border-primary shadow-lg shadow-primary/20 relative scale-105"
                  : "border-border"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    {t("pricingSection.plans.professional.badge")}
                  </span>
                </div>
              )}
              {plan.isLifetime && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">
                    {plan.isLifetime
                      ? plan.price
                      : billingPeriod === "yearly"
                      ? plan.yearlyPrice
                      : plan.price}
                  </span>
                  {!plan.isLifetime && (
                    <span className="text-muted-foreground ml-1">
                      /{billingPeriod === "yearly" ? "month" : "month"}
                    </span>
                  )}
                  {billingPeriod === "yearly" &&
                    !plan.isLifetime &&
                    plan.name !== t("pricingSection.plans.free.name") && (
                      <div className="text-sm text-muted-foreground">
                        Billed{" "}
                        {billingPeriod === "yearly"
                          ? `€${(
                              parseFloat(plan.yearlyPrice.replace("€", "")) * 12
                            ).toFixed(2)}/year`
                          : "monthly"}
                      </div>
                    )}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {plan.description}
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  variant={plan.highlighted ? "default" : "outline"}
                  onClick={() => handleSubscribe(plan.name)}
                  disabled={plan.name === t("pricingSection.plans.free.name")}
                >
                  {plan.name === t("pricingSection.plans.free.name")
                    ? t("pricingSection.plans.free.currentPlan")
                    : t("pricingSection.plans.free.getStarted")}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            {t("pricingSection.footer.text")}
          </p>
        </div>
      </div>
    </section>
  );
}
